import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { ToastService } from 'src/app/services/toast.service';
import { SocialFeedService } from '../../services/social-feed.service';
import { SocialPost } from '../../models/social-feed.model';

/**
 * One post, the same on every social page: author, where it was posted,
 * text (edited in place), photos / video / poll, like / comment / boost,
 * and its menu (edit, delete, pin, report) with their dialogs. The page
 * just lists posts; the card does the rest and tells the page when a post
 * was removed or its pin changed (so a group page can re-order).
 */
@Component({
  selector: 'app-social-post-card',
  templateUrl: './social-post-card.component.html',
  styleUrls: ['./social-post-card.component.scss'],
  standalone: false,
})
export class SocialPostCardComponent {
  @Input({ required: true }) post!: SocialPost;
  /** Show "in <group / community>" (off on that group's own page). */
  @Input() showSource = true;
  /** inline: comments open under the card; link: the comment button opens the post page; always: shown open (post page). */
  @Input() comments: 'inline' | 'link' | 'always' = 'link';
  /** Briefly highlight (arriving from a notification). */
  @Input() highlighted = false;

  @Output() removed = new EventEmitter<number>();
  @Output() pinChanged = new EventEmitter<SocialPost>();

  myId: number | null = null;
  commentsOpen = false;
  editing = false;
  editDraft = '';
  savingEdit = false;
  confirmingDelete = false;
  deleting = false;
  reporting = false;
  private likeBusy = false;
  private pinBusy = false;

  constructor(
    private social: SocialFeedService,
    private router: Router,
    private toaster: ToastService,
    private cdr: ChangeDetectorRef,
    currentUser: CurrentUserService
  ) {
    currentUser.currentUser.subscribe((u) => (this.myId = u?.id ?? null));
  }

  get sourceName(): string | null {
    return this.post.community_name || this.post.group_name || this.post.game_group_name || null;
  }

  get sourceIcon(): string {
    return this.post.community ? 'fa-users' : this.post.game_group ? 'fa-gamepad' : 'fa-map-o';
  }

  get canReport(): boolean {
    return this.post.post_type !== 'system' && this.post.author.id !== this.myId;
  }

  get isSystem(): boolean {
    return this.post.post_type === 'system';
  }

  get showComments(): boolean {
    return this.comments === 'always' || (this.comments === 'inline' && this.commentsOpen);
  }

  openSource(): void {
    const p = this.post;
    if (p.community_slug) this.router.navigate(['/communities', p.community_slug]);
    else if (p.group != null) this.router.navigate(['/social-group', p.group]);
    else if (p.game_group != null) this.router.navigate(['/social-game-group', p.game_group]);
  }

  openAuthor(): void {
    this.router.navigate(['/social-profile', this.post.author.id]);
  }

  openPost(): void {
    if (this.comments !== 'always') this.router.navigate(['/social-post', this.post.id]);
  }

  onComment(): void {
    if (this.comments === 'inline') this.commentsOpen = !this.commentsOpen;
    else if (this.comments === 'link') this.openPost();
  }

  toggleLike(): void {
    if (this.likeBusy) return;
    this.likeBusy = true;
    const post = this.post;
    const was = post.liked_by_me;
    post.liked_by_me = !was;
    post.likes_count += was ? -1 : 1;
    (was ? this.social.unlikePost(post.id) : this.social.likePost(post.id)).subscribe({
      next: (u) => {
        post.liked_by_me = u.liked_by_me;
        post.likes_count = u.likes_count;
        this.likeBusy = false;
        this.cdr.markForCheck();
      },
      error: () => {
        post.liked_by_me = was;
        post.likes_count += was ? 1 : -1;
        this.likeBusy = false;
        this.cdr.markForCheck();
      },
    });
  }

  boost(): void {
    const post = this.post;
    if (post.boosted_by_me || post.can_boost === false) return;
    post.boosted_by_me = true;
    post.boosts_count += 1;
    this.social.boostPost(post.id).subscribe({
      next: (u) => {
        post.boosts_count = u.boosts_count;
        this.toaster.showSuccess('Shared with your followers.');
        this.cdr.markForCheck();
      },
      error: () => {
        post.boosted_by_me = false;
        post.boosts_count -= 1;
        this.cdr.markForCheck();
      },
    });
  }

  togglePin(): void {
    if (this.pinBusy) return;
    this.pinBusy = true;
    const post = this.post;
    (post.is_pinned ? this.social.unpinPost(post.id) : this.social.pinPost(post.id)).subscribe({
      next: (u) => {
        post.is_pinned = u.is_pinned;
        this.pinBusy = false;
        this.pinChanged.emit(post);
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.pinBusy = false;
        this.toaster.showNote(err?.error?.error || "Couldn't change the pin. Please try again.", 'danger');
      },
    });
  }

  startEdit(): void {
    this.editing = true;
    this.editDraft = this.post.content;
  }

  saveEdit(): void {
    const text = this.editDraft.trim();
    if (!text || this.savingEdit) return;
    this.savingEdit = true;
    this.social.editPost(this.post.id, text).subscribe({
      next: (u) => {
        this.post.content = u.content;
        this.post.is_edited = u.is_edited;
        this.post.updated_at = u.updated_at;
        this.editing = false;
        this.savingEdit = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.savingEdit = false;
        this.cdr.markForCheck();
      },
    });
  }

  confirmDelete(): void {
    if (this.deleting) return;
    this.deleting = true;
    this.social.deletePost(this.post.id).subscribe({
      next: () => {
        this.deleting = false;
        this.confirmingDelete = false;
        this.removed.emit(this.post.id);
      },
      error: () => {
        this.deleting = false;
        this.cdr.markForCheck();
      },
    });
  }

  initials(name: string): string {
    return (name || '')
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  imageUrl(path: string | null | undefined): string {
    return this.social.resolveImageUrl(path);
  }

  timeAgo(isoDate: string): string {
    const minutes = Math.floor((Date.now() - new Date(isoDate).getTime()) / 60000);
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d`;
    return new Date(isoDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
}
